import type { CSSProperties } from "react"

/**
 * Правило цвета мастера: из цвета, который мастер выбрал себе сам, получается пара
 * «фон — текст», пригодная для чтения.
 *
 * ЗАЧЕМ ПРЕОБРАЗОВАНИЕ ВООБЩЕ. Мастер выбирает цвет-метку, а не цвет фона: он ставит
 * ярко-жёлтый #f2c94c, потому что это «его» цвет в журнале. Класть такой цвет фоном
 * под текст нельзя — читать перестанут оба. Поэтому тон берётся у мастера,
 * а светлота — у темы.
 *
 * ПОЧЕМУ СВЕТЛОТА В CSS, А НЕ ЗДЕСЬ. Она единственная, что меняется между светлой и тёмной
 * темой (92 % против 24 %). Отдав её переменной темы, мы получаем цвет мастера, который
 * переворачивается при переключении темы САМ, без пересчёта и без перерисовки.
 * Здесь остаётся ровно то, что тема не решает: разбор hex и нормализация ахроматики.
 */

/** Ниже этой насыщенности цвет считается серым: у серого нет тона, его нечего сохранять. */
const ACHROMATIC_THRESHOLD = 12
/** Тон, который подставляется серому. 28° — тёплая нейтраль дизайн-системы. */
const ACHROMATIC_HUE = 28
/** Насыщенность подставленной нейтрали: заметно, что это цвет, но это не цвет мастера. */
const ACHROMATIC_SATURATION = 14
/** Потолок насыщенности фона. Одинаков в обеих темах, поэтому применяется здесь. */
const MAX_SATURATION = 55

export interface MasterHue {
  /** Тон в градусах. */
  readonly hue: number
  /** Насыщенность в процентах, уже с потолком. */
  readonly saturation: number
  /** Светлота исходного цвета: по ней выбирается цвет букв на аватаре. */
  readonly lightness: number
}

function parseHex(color: string): { r: number; g: number; b: number } | null {
  const raw = color.trim().replace(/^#/, "")
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw.length === 8
        ? raw.slice(0, 6)
        : raw

  if (full.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(full)) return null

  return {
    r: Number.parseInt(full.slice(0, 2), 16) / 255,
    g: Number.parseInt(full.slice(2, 4), 16) / 255,
    b: Number.parseInt(full.slice(4, 6), 16) / 255,
  }
}

/** #RRGGBB → HSL. Компоненты в градусах и процентах, как их понимает CSS. */
export function hexToHsl(color: string): { h: number; s: number; l: number } | null {
  const rgb = parseHex(color)
  if (rgb === null) return null

  const max = Math.max(rgb.r, rgb.g, rgb.b)
  const min = Math.min(rgb.r, rgb.g, rgb.b)
  const delta = max - min
  const l = (max + min) / 2

  if (delta === 0) return { h: 0, s: 0, l: l * 100 }

  const s = delta / (1 - Math.abs(2 * l - 1))

  let h: number
  if (max === rgb.r) h = ((rgb.g - rgb.b) / delta) % 6
  else if (max === rgb.g) h = (rgb.b - rgb.r) / delta + 2
  else h = (rgb.r - rgb.g) / delta + 4

  h *= 60
  if (h < 0) h += 360

  return { h, s: s * 100, l: l * 100 }
}

/** Цвет мастера → тон и насыщенность для пары «фон — текст». Пустой цвет даёт нейтраль. */
export function masterHue(color: string | null | undefined): MasterHue {
  const hsl = color === null || color === undefined ? null : hexToHsl(color)
  if (hsl === null) {
    return { hue: ACHROMATIC_HUE, saturation: ACHROMATIC_SATURATION, lightness: 50 }
  }

  if (hsl.s < ACHROMATIC_THRESHOLD) {
    // Белый и почти чёрный дают один и тот же тон-заглушку: иначе колонка белого мастера
    // была бы неотличима от фона, а колонка чёрного — от линий сетки.
    return { hue: ACHROMATIC_HUE, saturation: ACHROMATIC_SATURATION, lightness: hsl.l }
  }

  return {
    hue: Math.round(hsl.h),
    saturation: Math.round(Math.min(hsl.s, MAX_SATURATION)),
    lightness: hsl.l,
  }
}

/**
 * Переменные для класса .plnr-master-tint. Ставятся на колонку журнала: дальше фон
 * и текст считает CSS, беря светлоту из темы.
 */
export function masterTintStyle(color: string | null | undefined): CSSProperties {
  const { hue, saturation } = masterHue(color)
  return {
    "--master-h": String(hue),
    "--master-s": `${saturation}%`,
  } as CSSProperties
}

/**
 * Аватар в шапке колонки. Это одно из двух мест, где цвет мастера показывается КАК ЕСТЬ,
 * без преобразования: маленький кружок — единственный способ увидеть выбранный цвет
 * целиком. Цвет букв выбирается по светлоте фона, иначе инициалы пропадут.
 */
export function masterAvatarStyle(color: string | null | undefined): CSSProperties {
  const { lightness } = masterHue(color)
  const background = color ?? "var(--bg-sunken)"
  return {
    background,
    color: lightness > 60 ? "var(--on-master-color-dark)" : "var(--on-master-color-light)",
  }
}

/** Инициалы по первым буквам двух первых слов имени. */
export function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).slice(0, 2)
  return words.map((word) => word.slice(0, 1).toUpperCase()).join("")
}
